import type React from "react";
import { type FC, type ReactNode } from "react";
import { Badge, Box, Paper, styled } from "~/common/mui";
import { NOTIFICATIONS_PAGE, PRODUCT_COMPARISON_PAGE, STORE_FINDER, WISHLIST_PAGE } from "~/utils/url/staticUrls";
import { canSeeSalesFinder } from "~/features/navigation/components/headerMenu/DesktopPersonalNav";
import { getProductComparisonNavigationEntryUrl } from "~/features/compareProducts/utils/productComparisonHelper";
import { useDispatch, useSelector } from "react-redux";
import { selectProductComparisonList } from "~/features/compareProducts/redux/productComparisonSelectors";
import { getWishlistNavigationEntryUrl, useLoadWishlistsOnMount } from "~/features/wishlist/utils/wishlistHelper";
import { selectWishlistAmount } from "~/features/wishlist/redux/wishlistSelectors";
import { selectNewNotificationAmount } from "~/features/notifications/redux/NotificationsSelectors";
import { Permission, userHasAnyPermission, userHasPermission } from "~/domains/user/utils/permissions";
import classNames from "classnames";
import { markNotificationsAsInteractedThunk } from "~/features/notifications/redux/actions";
import { useTranslation } from "react-i18next";
import { canSeeWishlistButton } from "~/utils/features";
import { MobileMinicart } from "~/features/cart/minicart/MinicartComponent";
import { useIsBottomNavigationVisible } from "~/features/BottomNavigation/BottomNavigationUtils";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CompareOutlinedIcon from "@mui/icons-material/CompareOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";

const BOTTOM_NAV_HEIGHT = 56;

const StyledPaper = styled(Paper)(({ theme }) => ({
  display: "flex",
  height: BOTTOM_NAV_HEIGHT,
  width: "100vw",
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  opacity: 0.92,
  background: theme.palette.surface.main,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderRadius: "0 !important",
  paddingBottom: 4,
  boxSizing: "border-box",
  zIndex: 1,
}));

const StyledList = styled("ul")({
  display: "inline-flex",
  justifyContent: "space-between",
  width: "100%",
  listStyleType: "none",
  margin: 0,
  padding: 0,
});

const StyledListItemContainer = styled("li")({
  flex: 1,
});

const StyledListItem = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isActive" && prop !== "$isNotification",
})<{ $isActive?: boolean; $isNotification?: boolean }>(({ theme, $isActive, $isNotification }) => ({
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 50,
  height: "100%",
  padding: "0 4px",
  color: $isActive ? theme.palette.primary.main : theme.palette.text.primary,
  borderTop: $isActive ? `3px solid ${theme.palette.primary.main}` : undefined,
  "& .MuiBadge-badge": {
    ...theme.typography.labelMedium,
    fontWeight: "bold",
    top: 6,
    right: -4,
    boxShadow: "none !important",
    backgroundColor: $isNotification ? theme.palette.salesCampaign.main : theme.palette.primary.main,
    color: `${theme.palette.common.white} !important`,
  },
}));

function BottomNavigationSection() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const listItems = useSelector(selectProductComparisonList).length;

  const showWishlistMenuEntry = canSeeWishlistButton();
  const wishlistUrl = getWishlistNavigationEntryUrl();
  const productComparisonUrl = getProductComparisonNavigationEntryUrl();
  useLoadWishlistsOnMount();
  const wishlistAmount = useSelector(selectWishlistAmount);

  const newNotificationAmount = useSelector(selectNewNotificationAmount);

  const canSeeCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);

  if (!useIsBottomNavigationVisible()) {
    return null;
  }

  return (
    <StyledPaper variant={"elevation"} elevation={3}>
      <StyledList>
        <StyledListItemContainer>
          <a href={productComparisonUrl} id="navigation_comparison_list_btn" data-test-id={"navigation-wishlist"}>
            <BottomNavigationItem
              data-test-id={"navigation-btn-sales-partner"}
              quantity={listItems}
              icon={<CompareOutlinedIcon />}
              name={t("header.link.comparison.list")}
              isActive={window.location.pathname.endsWith(PRODUCT_COMPARISON_PAGE)}
            />
          </a>
        </StyledListItemContainer>
        {showWishlistMenuEntry && (
          <StyledListItemContainer>
            <a href={wishlistUrl} id="navigation_wishlist_btn" data-test-id={"navigation-wishlist"}>
              <BottomNavigationItem
                name={t("wishlistPage.title")}
                icon={<BookmarkBorderOutlinedIcon />}
                data-test-id={"navigation-btn-wishlist"}
                quantity={wishlistAmount !== undefined ? wishlistAmount : ""}
                isActive={window.location.pathname.endsWith(WISHLIST_PAGE)}
              />
            </a>
          </StyledListItemContainer>
        )}
        {userHasPermission(Permission.SEE_NOTIFICATIONS) && (
          <StyledListItemContainer>
            <a href={NOTIFICATIONS_PAGE} id="navigation_notification_btn" data-test-id={"navigation-notification"}>
              <BottomNavigationItem
                name={t("header.link.my.notification")}
                data-test-id={"navigation-btn-notification"}
                icon={<NotificationsOutlinedIcon />}
                quantity={newNotificationAmount}
                onClick={() => dispatch(markNotificationsAsInteractedThunk())}
                isActive={window.location.pathname.endsWith(NOTIFICATIONS_PAGE)}
                isNotification
              />
            </a>
          </StyledListItemContainer>
        )}
        {canSeeSalesFinder() && (
          <StyledListItemContainer>
            <a href={STORE_FINDER} data-test-id={"navigation-sales-partner"}>
              <BottomNavigationItem
                data-test-id={"navigation-btn-sales-partner"}
                icon={<LocationOnOutlinedIcon />}
                name={t("salesPartner.title")}
                isActive={window.location.pathname.endsWith(STORE_FINDER)}
              />
            </a>
          </StyledListItemContainer>
        )}
        {canSeeCart && (
          <StyledListItemContainer>
            <MobileMinicart />
          </StyledListItemContainer>
        )}
      </StyledList>
    </StyledPaper>
  );
}

interface BottomNavigationItemProps {
  name: string;
  icon?: ReactNode;
  isActive?: boolean;
  isNotification?: boolean;
  quantity?: string | number;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  "data-test-id"?: string;
}

export const BottomNavigationItem: FC<BottomNavigationItemProps> = (props) => (
  <StyledListItem
    $isActive={props.isActive}
    $isNotification={props.isNotification}
    onClick={props.onClick}
    data-test-id={props["data-test-id"]}
  >
    <>
      <Badge badgeContent={props.quantity ? props.quantity : null}>
        {props.icon && props.icon}
      </Badge>
      {props.name && <Box className={"label-medium f-no-wrap"}>{props.name}</Box>}
    </>
  </StyledListItem>
);

export function createBottomNavigation() {
  return <BottomNavigationSection />;
}
